<script lang="ts">
	import { t } from '../../i18n/i18n'
	import type { Lot } from './lot'
	import { getContext, onMount } from 'svelte'

	import Salutation from '../atoms/Salutation.svelte'
	import InfoCard from '../organisms/InfoCard.svelte'
	import ProjectInformationGrid from '../project/ProjectInformationGrid.svelte'
	import TextArea from '../atoms/TextArea.svelte'
	import FileUpload from '../organisms/FileUpload.svelte'
	import ProjectNavigationButtons from '../molecules/ProjectNavigationButtons.svelte'
	import { userInfo, userIsLoggedIn } from '../stores'
	import PublicPageHeader from '../molecules/PublicPageHeader.svelte'
	import PublicPageFooter from '../molecules/PublicPageFooter.svelte'
	import Basic from '../templates/Basic.svelte'
	import ContactBox from '../atoms/ContactBox.svelte'
	import { type Offers, OfferState } from '../bid/types/offer'
	import { type Writable } from 'svelte/store'
	import { type RequestDto } from '../bid/types/requestDto'
	import type { ContactData } from '../types/contactData'
	import { loadOfferAttachments } from '../bid/offerUtils'
	import { LotState, LotType, OPTIMIZED_STATES_WITHOUT_DISCARDED } from '../types/enums'
	import iconProjectCompleted from '@/icons/icon_pubpage_project_completed.png'
	import type { PubPageParams } from '../bid/types/pubPageParams'
	import { NextButtonWantedObject } from '../bid/types/nextButtonDto'
	import type { Deal } from '../bid/types/deal'

	const lot: Writable<Lot> = getContext('lot')
	const offers: Writable<Offers> = getContext('offers')
	const request: Writable<RequestDto> = getContext('request')
	const urlParams: PubPageParams = getContext('urlParams')
	const deal: Writable<Deal> = getContext('deal')

	export let recipientData: ContactData
	export let projectNavigationCurrentId: string = $request.publicId
	export let projectNavigationReturnUrl: string | undefined = undefined
	export let projectNavigationWantedObject: NextButtonWantedObject = NextButtonWantedObject.REQUEST
	export let attachmentsReadonly: boolean = false

	let attachmentsOffer: any[] = []
	let attachmentsUploadUrl: string | string[]
	let attachmentLinkUrl: string
	let linkAttachments

	let currentUserInfo: { userLogin: string; tenantName: string }
	userInfo.subscribe((value) => (currentUserInfo = value))

	let completed: boolean = $lot.state === LotState.COMPLETED
	let showBlur: boolean = completed
	const toggleBlur = () => (showBlur = !showBlur)

	onMount(() => {
		updateUrl()
	})

	function reloadAttachments(data: any) {
		if ((data?.length || -1) > 0) {
			attachmentsOffer = data
		}
	}

	function handleUploadOfferAttachmentWithoutOffer(responseData?: Offers) {
		// Set publicId of draft offer if an attachment was uploaded without an offer, s.t. the uploaded attachment is
		// fetched correctly without reloading the page.
		if (Object.values($offers).every((offer) => !offer?.publicId) && responseData) {
			Object.keys($offers).forEach((key) => {
				if (responseData[key]) {
					$offers[key].publicId = responseData[key]?.publicId
					$offers[key].offerState = OfferState.DRAFT
				}
			})
		}
	}

	function updateUrl() {
		attachmentsUploadUrl = `/api/request/${urlParams.requestPublicId}/requested/${urlParams.requestedEntityType}/${urlParams.requestedEntityId}/user/${urlParams.offerUserPublicId}/attachment/new`
		if (Object.values($offers).some((offer) => offer?.publicId)) {
			const id =
				$lot.type === LotType.TRANSPORT
					? $offers.transport?.publicId
					: $offers.material?.publicId || $offers.materialWithTransport?.publicId
			attachmentsUploadUrl = `/api/offer/${id}/attachment/new`
		}
	}

	$: ($lot.state, (completed = $lot.state === LotState.COMPLETED))
	$: (completed, (showBlur = completed))
	$: ($offers.material?.publicId,
		$offers.materialWithTransport?.publicId,
		$offers?.transport?.publicId,
		updateUrl())
</script>

{#if completed}
	<div class="blur-background-overlay" class:hide={!showBlur}>
		<div class="blur-background-white"></div>
		<div class="blur-background-overlay-content align-start">
			<span class="completed-title">{$t('UI.optimizerPage.project.completed.title')}</span>
			<img
				class="completed-img"
				alt={$t('UI.optimizerPage.project.completed.title')}
				src={iconProjectCompleted}
			/>
			<div style="margin-top: 15px; max-width: 35%">
				<span class="completed-hint">{$t('UI.optimizerPage.project.completed.hint')} </span>
			</div>
			<div style="margin-top: 15px">
				<span class="show-project-details" on:click={toggleBlur}
					>{$t('Project.editProjectData')}</span
				>
			</div>
		</div>
	</div>
{/if}

{#if !$userIsLoggedIn}
	<PublicPageHeader />
{/if}

<Basic showStandardHeaderFooter={$userIsLoggedIn}>
	<div class="container-salutation-contact">
		<Salutation name={recipientData.fullName} />
		{#if $userIsLoggedIn}
			<ContactBox />
		{/if}
	</div>
	<div class="row">
		<div class="col-lg-5 col-md-12 col-sm-12">
			<InfoCard />
		</div>
		<div class="col-lg-7 col-md-12 col-sm-12">
			<div class="container-project-information">
				<ProjectInformationGrid title={$t('UI.project.information')} {...$lot} />
				<TextArea
					title={$t('UI.materialDetailsComment.header')}
					text={$lot.lotRemarks}
					placeholder={$t('UI.remarks.noRemarks')}
					readonly
				/>
				{#if $lot.state !== LotState.DISCARDED}
					<TextArea
						title={$t('UI.projectDetailsComment.header')}
						text={$lot.projectRemarks}
						placeholder={$t('UI.remarks.noRemarks')}
						readonly
						isHtml
					/>
				{/if}
			</div>
		</div>
	</div>
	{#if OPTIMIZED_STATES_WITHOUT_DISCARDED.includes($lot.state)}
		<div class="row">
			<div class="col-lg-12 col-sm-12">
				<TextArea
					id="optimizerFurtherDetailsComment"
					title={$t('UI.Model.moreDetails')}
					text={$deal?.furtherDetailsComment}
					placeholder={$t('UI.remarks.noRemarks')}
					readonly
				/>
			</div>
		</div>
	{/if}
	<div class="row" style="margin-top: 1.875rem">
		<div class="col-lg-12 col-sm-12">
			<FileUpload
				bind:eventByAccept={linkAttachments}
				uploadUrl={attachmentsUploadUrl}
				filePrefix={$t('UI.attachments.optimizer.prefix') +
					currentUserInfo.tenantName +
					'_' +
					currentUserInfo.userLogin +
					'-'}
				visibleToCustomer={0}
				visibleToPartner={1}
				linkUrl={attachmentLinkUrl}
				updateFun={() =>
					loadOfferAttachments($offers, urlParams).then((data) => reloadAttachments(data))}
				attachments={attachmentsOffer}
				parentPublicId={$offers?.material.publicId}
				readOnly={attachmentsReadonly}
				canNotDelete={completed ||
					Object.values($offers).some((offer) => offer?.offerState !== OfferState.DRAFT)}
				canNotUpload={completed}
				onUploadComplete={handleUploadOfferAttachmentWithoutOffer}
			/>
		</div>
	</div>

	<slot {linkAttachments} />

	<div class="row">
		<div class="col-lg-12 col-sm-12">
			<ProjectNavigationButtons
				currentId={projectNavigationCurrentId}
				returnUrl={projectNavigationReturnUrl}
				wantedObject={projectNavigationWantedObject}
				isSaas={false}
			/>
		</div>
	</div>
</Basic>

{#if !$userIsLoggedIn}
	<PublicPageFooter />
{/if}

<style lang="scss">
	@import '$styles/animations';
	@import '$styles/pubPage';

	.columns {
		@include flex-col(1.875rem, $alignment: flex-end);

		width: 100%;
	}

	.completed-title {
		@include roboto-font(28px, 500, 24px, black);

		text-shadow: 0 4px 4px rgba(0, 0, 0, 0.25);
		font-weight: bold;
	}

	.completed-hint {
		@include roboto-font(18px, 400, 0.875rem, black);

		margin-bottom: 8rem;
	}

	.completed-img {
		margin-left: 25px;

		@media screen and (max-width: $small) {
			width: 33%;
			max-width: 200px;
		}
	}

	.hide {
		z-index: -10;
		display: none;
	}

	.show-project-details {
		@include roboto-font(15px, 500, 1rem, $primaryGreen);

		cursor: pointer;
	}
</style>
